import { BrowserModule } from '@angular/platform-browser';
import { LOCALE_ID, NgModule } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeRu from '@angular/common/locales/ru';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '@apps/clos-sessions/environments';
import { TranslateModule } from '@ngx-translate/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgxsModule } from '@ngxs/store';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { AngularFireModule } from '@angular/fire';
import { AngularFireAuthModule } from '@angular/fire/auth';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { AppHttpInterceptor } from './services/http-interceptor';
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator';
import { PopupModule } from '@apps/clos-sessions/shared/popup/popup.module';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsDataPluginModule } from '@ngxs-labs/data';

import {
  AngularFireAnalyticsModule,
  APP_VERSION,
  COLLECTION_ENABLED,
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/analytics';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppVersionUpdateService } from '@apps/clos-sessions/services/app-version-update';
import { UserPreferencesState } from './state/user-preferences';
import { ProcessingState } from './state/processing';
import { SubscriptionsState } from './state/subscriptions';
import { RemoteSessionsState } from './state/remote-sessions';
import { AuthState } from './state/auth';
import { CloudState } from './state/cloud';
import { ArchivesState } from '@apps/clos-sessions/state/archives';
import { NgxIndexedDBModule } from 'ngx-indexed-db';
import { ArchiveAssetsState } from '@apps/clos-sessions/state/archive-assets';
import { storeOptions } from './state/store-options';
import { MEDIA_PROVIDER_OVERRIDE_CAPTURE } from './routes/rooms/routes/room/services/media-provider-override';
registerLocaleData(localeRu, 'ru');

@NgModule({
  declarations: [AppComponent],
  imports: [
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
    }),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAuthModule,
    AngularFireAnalyticsModule,
    NgxsModule.forRoot(
      [
        AuthState,
        UserPreferencesState,
        ProcessingState,
        SubscriptionsState,
        RemoteSessionsState,
        ArchivesState,
        ArchiveAssetsState,
        CloudState,
      ],
      {
        developmentMode: !environment.production,
        ...storeOptions,
      },
    ),
    NgxsStoragePluginModule.forRoot({
      key: [AuthState, UserPreferencesState],
      migrations: [...UserPreferencesState.migrations],
    }),
    NgxsDataPluginModule.forRoot(),
    NgxsRouterPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsDispatchPluginModule.forRoot(),
    TranslateModule.forRoot(),

    NgxIndexedDBModule.forRoot({
      name: 'clos-sessions',
      version: 1,
      objectStoresMeta: [
        {
          store: environment.indexDb.collections.assets,
          storeConfig: {
            keyPath: 'id',
            autoIncrement: true,
          },
          storeSchema: [
            { name: 'name', keypath: 'name', options: { unique: false } },
            { name: 'size', keypath: 'size', options: { unique: false } },
            { name: 'type', keypath: 'type', options: { unique: false } },
            { name: 'createAt', keypath: 'createAt', options: { unique: false } },
            { name: 'binary', keypath: 'binary', options: { unique: false } },
          ],
        },
      ],
    }),
    PopupModule.forRoot(),
  ],
  providers: [
    {
      provide: APP_VERSION,
      useValue: environment.version,
    },
    UserTrackingService,
    ScreenTrackingService,
    {
      provide: COLLECTION_ENABLED,
      useValue: environment.production,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppHttpInterceptor,
      multi: true,
    },
    { provide: LOCALE_ID, useValue: 'ru' },
    MEDIA_PROVIDER_OVERRIDE_CAPTURE,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(updateService: AppVersionUpdateService, processingState: ProcessingState) {
    updateService.init();
    processingState.connect().subscribe();
  }
}
